import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:4040/api";

const emptyForm = {
  name: "",
  code: "",
  type: "",
  status: "active",
  routes: "",
  mmsi: "",
  notes: "",
  length: "",
  beam: "",
  draft: "",
  dwt: "",
  cargoCapacity: "",
  speed: "",
  mainCrane: "",
  hiabCrane: "",
  reeferPlugs: "",
  rampCapacity: "",
  deckLoading: "",
  maxBookingWeight: "",
  maxLivestockAllocation: "",
  maxFreightAllocation: "",
  maxTEU: "",
  mixedCargoAllowed: "Yes",
  livestockAndFreightTogether: "Yes",
  safetyBuffer: "",
  operationalBuffer: "",
};

const EditVessel = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState(emptyForm);
  const [zones, setZones] = useState([]); // livestock pens
  const [holds, setHolds] = useState([]); // freight holds/decks
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const cleanNumber = (value) =>
    Number(String(value || "").replace(/[^\d.]/g, "")) || 0;

  useEffect(() => {
    async function loadVessel() {
      try {
        setLoading(true);

        const res = await fetch(`${API_BASE}/vessels/${id}`);

        if (!res.ok) {
          throw new Error("Failed to load vessel");
        }

        const data = await res.json();
        const vessel = data?.data || data;

        setForm({
          name: vessel.name || "",
          code: vessel.code || "",
          type: vessel.vesselType || "",
          status: vessel.status || "active",
          routes: vessel.routes || "",
          mmsi: vessel.mmsi || "",
          notes: vessel.notes || "",
          length: vessel.lengthM || "",
          beam: vessel.beamM || "",
          draft: vessel.draftM || "",
          dwt: vessel.grossTonnage || "",
          cargoCapacity: vessel.maxWeightKg || "",
          speed: vessel.speed || "",
          mainCrane: vessel.mainCrane || "",
          hiabCrane: vessel.hiabCrane || "",
          reeferPlugs: vessel.reeferPlugs || "",
          rampCapacity: vessel.rampCapacity || "",
          deckLoading: vessel.deckLoading || "",
          maxBookingWeight: vessel.maxWeightKg || "",
          maxLivestockAllocation: vessel.maxLivestockHead || "",
          maxFreightAllocation: vessel.maxCubeM3 || "",
          maxTEU: vessel.maxTEU || "",
          mixedCargoAllowed: vessel.mixedCargoAllowed || "Yes",
          livestockAndFreightTogether:
            vessel.livestockAndFreightTogether || "Yes",
          safetyBuffer: vessel.safetyBuffer || "",
          operationalBuffer: vessel.operationalBuffer || "",
        });

        const allZones = Array.isArray(vessel.zones) ? vessel.zones : [];
        // Split by whether this zone has a real pen number (belongs in the
        // Port/Starboard row sequence) rather than purely by type — a
        // freight-type zone that's actually a numbered pen (e.g. a
        // container slot replacing a livestock pen) should stay grouped
        // with the other numbered pens here, not get diverted into the
        // generic Holds section below (which has no Pen Number/Row concept
        // at all, just Deck Location).
        const penZones = allZones.filter(z => z.penNumber || (z.type !== 'freight' && z.type !== 'hold'));
        const holdZones = allZones.filter(z => !z.penNumber && (z.type === 'freight' || z.type === 'hold'));

        setZones(
          penZones.map((zone) => ({
            id: zone._id || Date.now() + Math.random(),
            penNumber: zone.penNumber || "",
            name: zone.name || "",
            row: zone.row || "",
            type: zone.type || "livestock",
            isHospitalPen: zone.isHospitalPen || false,
            isSpare: zone.isSpare || false,
            areaM2: zone.areaM2 || "",
            maxWeight: zone.maxWeightKg || "",
            maxUnits: zone.maxHeadCount || "",
            maxTEU: zone.maxTEU || "",
            notes: zone.notes || "",
          }))
        );

        setHolds(
          holdZones.map((zone) => ({
            id: zone._id || Date.now() + Math.random(),
            name: zone.name || "",
            deckLocation: zone.deckLocation || zone.row || "",
            maxWeightKg: zone.maxWeightKg || "",
            maxCubeM3: zone.maxCubeM3 || "",
            maxTEU: zone.maxTEU || "",
            notes: zone.notes || "",
          }))
        );
      } catch (err) {
        console.error(err);
        setMessage("Could not load vessel.");
      } finally {
        setLoading(false);
      }
    }

    loadVessel();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateZone = (zoneId, field, value) => {
    setZones((prev) =>
      prev.map((zone) =>
        zone.id === zoneId ? { ...zone, [field]: value } : zone
      )
    );
  };

  const addZone = () => {
    setZones((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: "",
        code: "",
        type: "freight",
        maxWeight: "",
        maxUnits: "",
        allowedCargo: "",
        notes: "",
      },
    ]);
  };

  const removeZone = (zoneId) => {
    setZones((prev) => prev.filter((zone) => zone.id !== zoneId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setMessage("Vessel name is required.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");

      const payload = {
        name: form.name,
        code: form.code,
        status: form.status,
        vesselType: form.type || "coastal-freight",
        mmsi: form.mmsi || "",
        routes: form.routes || "",

        maxWeightKg: cleanNumber(form.maxBookingWeight),
        maxLivestockHead: cleanNumber(form.maxLivestockAllocation),
        maxCubeM3: cleanNumber(form.maxFreightAllocation),

        notes: form.notes,

        zones: [
          ...zones
            .filter((zone) => zone.name.trim())
            .map((zone) => ({
              penNumber: zone.penNumber || "",
              name: zone.name,
              row: zone.row || "",
              type: zone.type || "livestock",
              isHospitalPen: Boolean(zone.isHospitalPen),
              isSpare: Boolean(zone.isSpare),
              areaM2: cleanNumber(zone.areaM2),
              maxWeightKg: cleanNumber(zone.maxWeight),
              maxHeadCount: cleanNumber(zone.maxUnits),
              maxTEU: cleanNumber(zone.maxTEU),
              notes: zone.notes || "",
            })),
          ...holds
            .filter((hold) => hold.name.trim())
            .map((hold) => ({
              name: hold.name,
              type: "freight",
              deckLocation: hold.deckLocation || "",
              row: hold.deckLocation || "",
              maxWeightKg: cleanNumber(hold.maxWeightKg),
              maxCubeM3: cleanNumber(hold.maxCubeM3),
              maxTEU: cleanNumber(hold.maxTEU),
              maxHeadCount: 0,
              notes: hold.notes || "",
            })),
        ],
      };

      const res = await fetch(`${API_BASE}/vessels/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Failed to update vessel");
      }

      navigate("/dashboard/settings");
    } catch (err) {
      console.error(err);
      setMessage("Could not save vessel. Check API and required fields.");
    } finally {
      setSaving(false);
    }
  };

  const messageIsError =
    message &&
    (message.toLowerCase().includes("could not") ||
      message.toLowerCase().includes("failed") ||
      message.toLowerCase().includes("required"));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-600">Loading vessel...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate("/dashboard/settings")}
              className="mb-4 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              ← Back
            </button>

            <h1 className="text-3xl font-bold tracking-tight text-slate-950">
              Edit Vessel
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Update vessel details, capacity settings, and zone configuration.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/dashboard/settings")}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              form="edit-vessel-form"
              disabled={saving}
              className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Vessel"}
            </button>
          </div>
        </div>

        {message ? (
          <div
            className={`mb-6 rounded-2xl border px-4 py-3 text-sm shadow-sm ${
              messageIsError
                ? "border-red-200 bg-red-50 text-red-700"
                : "border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}
          >
            {message}
          </div>
        ) : null}

        <form id="edit-vessel-form" onSubmit={handleSubmit}>
          <div className="grid gap-6 xl:grid-cols-[1.7fr_0.9fr]">
            <div className="space-y-6">
              <SectionCard
                title="Basic Details"
                description="Core vessel profile, status, routes, and operating notes."
              >
                <SettingsGrid>
                  <Field label="Vessel Name">
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Internal Code">
                    <input
                      name="code"
                      value={form.code}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Vessel Type">
                    <input
                      name="type"
                      value={form.type}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Status">
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      className="input"
                    >
                      <option value="active">Active</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </Field>

                  <Field label="Default Operating Routes">
                    <input
                      name="routes"
                      value={form.routes}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>
                </SettingsGrid>

                <div className="mt-4">
                  <Field label="MMSI (for live vessel tracking)">
                    <input
                      name="mmsi"
                      value={form.mmsi}
                      onChange={handleChange}
                      className="input"
                      placeholder="e.g. 512345678"
                      maxLength={9}
                    />
                    <p className="mt-1 text-xs text-slate-400">
                      9-digit Maritime Mobile Service Identity number. Find it on{" "}
                      <a href="https://www.marinetraffic.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        MarineTraffic
                      </a>{" "}
                      by searching the vessel name. Used to show a live tracking map on the sailing page and customer portal.
                    </p>
                    {form.mmsi && form.mmsi.length === 9 && (
                      <a
                        href={`https://www.marinetraffic.com/en/ais/details/ships/mmsi:${form.mmsi}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-block text-xs font-medium text-blue-600 hover:underline"
                      >
                        Open on MarineTraffic →
                      </a>
                    )}
                  </Field>
                </div>

                <Field label="Notes">
                  <textarea
                    name="notes"
                    rows={3}
                    value={form.notes}
                    onChange={handleChange}
                    className="input min-h-[110px] resize-y"
                  />
                </Field>
              </SectionCard>

              <SectionCard
                title="Technical Specs"
                description="Technical dimensions, lifting gear, and vessel handling capability."
              >
                <SettingsGrid>
                  <Field label="Length Overall / LOA metres">
                    <input
                      name="length"
                      value={form.length}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Beam / Breadth metres">
                    <input
                      name="beam"
                      value={form.beam}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Draft metres">
                    <input
                      name="draft"
                      value={form.draft}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Gross Tonnage / DWT">
                    <input
                      name="dwt"
                      value={form.dwt}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Cargo Capacity">
                    <input
                      name="cargoCapacity"
                      value={form.cargoCapacity}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Speed">
                    <input
                      name="speed"
                      value={form.speed}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Main Crane Capacity">
                    <input
                      name="mainCrane"
                      value={form.mainCrane}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Secondary Lifting Capacity">
                    <input
                      name="hiabCrane"
                      value={form.hiabCrane}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Reefer Plugs">
                    <input
                      name="reeferPlugs"
                      value={form.reeferPlugs}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Ramp Capacity">
                    <input
                      name="rampCapacity"
                      value={form.rampCapacity}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Deck Loading Rating">
                    <input
                      name="deckLoading"
                      value={form.deckLoading}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>
                </SettingsGrid>
              </SectionCard>

              <SectionCard
                title="Booking Capacity"
                description="Set vessel booking limits and allocation rules."
              >
                <SettingsGrid>
                  <Field label="Max Booking Weight KG">
                    <input
                      name="maxBookingWeight"
                      value={form.maxBookingWeight}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Max Livestock Head">
                    <input
                      name="maxLivestockAllocation"
                      value={form.maxLivestockAllocation}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Max Freight / Cube">
                    <input
                      name="maxFreightAllocation"
                      value={form.maxFreightAllocation}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Max TEU">
                    <input
                      name="maxTEU"
                      value={form.maxTEU}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Mixed Cargo Allowed">
                    <select
                      name="mixedCargoAllowed"
                      value={form.mixedCargoAllowed}
                      onChange={handleChange}
                      className="input"
                    >
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </Field>

                  <Field label="Livestock + Freight Together">
                    <select
                      name="livestockAndFreightTogether"
                      value={form.livestockAndFreightTogether}
                      onChange={handleChange}
                      className="input"
                    >
                      <option>Yes</option>
                      <option>No</option>
                    </select>
                  </Field>

                  <Field label="Safety Buffer %">
                    <input
                      name="safetyBuffer"
                      value={form.safetyBuffer}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>

                  <Field label="Operational Buffer %">
                    <input
                      name="operationalBuffer"
                      value={form.operationalBuffer}
                      onChange={handleChange}
                      className="input"
                    />
                  </Field>
                </SettingsGrid>
              </SectionCard>

              {/* Livestock Pens */}
              <SectionCard
                title="Livestock Pens"
                description="Configure livestock pens for load planning and MPI compliance."
                action={
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (zones.length > 0 && !window.confirm("This will replace all current pens with the updated William Fraser layout (P12+P13 converted to a 20' Container Slot, real areas applied). Continue?")) return;
                        const WF_PENS = [
                          { penNumber: "P1", name: "Pen P1", row: "port", type: "livestock", areaM2: 19.1, maxHeadCount: 20, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P2", name: "Pen P2", row: "port", type: "livestock", areaM2: 19.1, maxHeadCount: 20, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P3", name: "Pen P3", row: "port", type: "livestock", areaM2: 19.1, maxHeadCount: 20, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P4", name: "Pen P4", row: "port", type: "livestock", areaM2: 19.1, maxHeadCount: 20, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P5", name: "Pen P5", row: "port", type: "livestock", areaM2: 14.3, maxHeadCount: 15, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P6", name: "Pen P6", row: "port", type: "livestock", areaM2: 14.3, maxHeadCount: 15, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P7", name: "Pen P7", row: "port", type: "livestock", areaM2: 19.1, maxHeadCount: 20, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P8", name: "Pen P8", row: "port", type: "livestock", areaM2: 19.1, maxHeadCount: 20, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P9", name: "Pen P9", row: "port", type: "livestock", areaM2: 19.1, maxHeadCount: 20, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P10", name: "Pen P10", row: "port", type: "livestock", areaM2: 19.1, maxHeadCount: 20, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P11", name: "Pen P11", row: "port", type: "livestock", areaM2: 15.9, maxHeadCount: 16, isHospitalPen: false, isSpare: false, notes: "" },
                          { penNumber: "P12", name: "20' Container Slot", row: "port", type: "freight", areaM2: 26.0, maxHeadCount: 0, maxTEU: 1, isHospitalPen: false, isSpare: false, notes: "Converted from P12+P13 — a 20' container is exactly 1 TEU" },
                          { penNumber: "P14", name: "Pen P14", row: "port", type: "livestock", areaM2: 13.0, maxHeadCount: 9, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "P15", name: "Pen P15", row: "port", type: "livestock", areaM2: 13.0, maxHeadCount: 9, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "P16", name: "Pen P16 (Spare)", row: "port", type: "spare", areaM2: 3.0, maxHeadCount: 5, isHospitalPen: false, isSpare: true, notes: "Area is a rough estimate — the drawing didn't clearly dimension this pen" },
                          { penNumber: "S1", name: "Pen S1", row: "starboard", type: "livestock", areaM2: 13.0, maxHeadCount: 13, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S2", name: "Pen S2", row: "starboard", type: "livestock", areaM2: 13.0, maxHeadCount: 13, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S3", name: "Pen S3", row: "starboard", type: "livestock", areaM2: 13.0, maxHeadCount: 13, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S4", name: "Pen S4", row: "starboard", type: "livestock", areaM2: 13.0, maxHeadCount: 13, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S5", name: "Pen S5", row: "starboard", type: "livestock", areaM2: 13.0, maxHeadCount: 13, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S6", name: "Pen S6", row: "starboard", type: "livestock", areaM2: 13.0, maxHeadCount: 13, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S7", name: "Pen S7", row: "starboard", type: "livestock", areaM2: 13.0, maxHeadCount: 13, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S8", name: "Pen S8", row: "starboard", type: "livestock", areaM2: 13.0, maxHeadCount: 13, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S9", name: "Pen S9", row: "starboard", type: "livestock", areaM2: 13.0, maxHeadCount: 13, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S10", name: "Pen S10", row: "starboard", type: "livestock", areaM2: 14.0, maxHeadCount: 14, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S11", name: "Pen S11", row: "starboard", type: "livestock", areaM2: 14.0, maxHeadCount: 14, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S12", name: "Pen S12", row: "starboard", type: "livestock", areaM2: 12.0, maxHeadCount: 12, isHospitalPen: false, isSpare: false, notes: "Area is a best-guess estimate — not confirmed against real measurements" },
                          { penNumber: "S13", name: "Pen S13 (Hospital)", row: "starboard", type: "hospital", areaM2: 2.0, maxHeadCount: 3, isHospitalPen: true, isSpare: false, notes: "Hospital / spare pen — area is a rough estimate, drawing didn't clearly dimension it" },
                        ];
                        setZones(WF_PENS.map(p => ({ ...p, id: Date.now() + Math.random(), maxWeight: p.maxHeadCount * 450, maxUnits: p.maxHeadCount })));
                      }}
                      className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      Load WF Updated Layout (P12+P13 → 20' Container)
                    </button>
                    <button
                      type="button"
                      onClick={() => setZones(prev => [...prev, { id: Date.now(), penNumber: "", name: "", row: "", type: "livestock", isHospitalPen: false, isSpare: false, areaM2: "", maxWeight: "", maxUnits: "", maxTEU: "", notes: "" }])}
                      className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                    >
                      Add Pen
                    </button>
                  </div>
                }
              >
                <div className="space-y-4">
                  {zones.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                      No pens configured yet. Add one manually or load the WF default layout.
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 mb-2">
                      {zones.length} pens · {zones.reduce((s, z) => s + Number(z.maxUnits || 0), 0)} head total capacity
                    </div>
                  )}

                  {zones.map((zone, index) => (
                    <div key={zone.id}
                      className={`rounded-2xl border p-4 ${zone.isHospitalPen ? "border-red-200 bg-red-50" : zone.isSpare ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 text-sm">{zone.name || `Pen ${index + 1}`}</h3>
                          {zone.isHospitalPen && <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">Hospital</span>}
                          {zone.isSpare && <span className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">Spare</span>}
                        </div>
                        <button type="button" onClick={() => setZones(prev => prev.filter(z => z.id !== zone.id))}
                          className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        <Field label="Pen Number">
                          <input className="input" value={zone.penNumber || ""} onChange={(e) => updateZone(zone.id, "penNumber", e.target.value)} />
                        </Field>
                        <Field label="Pen Name">
                          <input className="input" value={zone.name} onChange={(e) => updateZone(zone.id, "name", e.target.value)} />
                        </Field>
                        <Field label="Row">
                          <select className="input" value={zone.row || ""} onChange={(e) => updateZone(zone.id, "row", e.target.value)}>
                            <option value="">— Select —</option>
                            <option value="port">Port</option>
                            <option value="starboard">Starboard</option>
                            <option value="centre">Centre</option>
                          </select>
                        </Field>
                        <Field label="Type">
                          <select className="input" value={zone.type} onChange={(e) => updateZone(zone.id, "type", e.target.value)}>
                            <option value="livestock">Livestock</option>
                            <option value="hospital">Hospital Pen</option>
                            <option value="spare">Spare</option>
                            <option value="freight">Freight / Container Slot</option>
                          </select>
                        </Field>
                        <Field label="Area (m²)">
                          <input className="input" type="number" value={zone.areaM2 || ""} onChange={(e) => updateZone(zone.id, "areaM2", e.target.value)} />
                        </Field>
                        <Field label="Max Head Count (fallback only — used only if Area is blank)">
                          <input className="input" type="number" value={zone.maxUnits} onChange={(e) => updateZone(zone.id, "maxUnits", e.target.value)} />
                        </Field>
                        <Field label="Max Weight (kg)">
                          <input className="input" type="number" value={zone.maxWeight} onChange={(e) => updateZone(zone.id, "maxWeight", e.target.value)} />
                        </Field>
                        <Field label="Max TEU (freight/container slots only)">
                          <input className="input" type="number" value={zone.maxTEU || ""} onChange={(e) => updateZone(zone.id, "maxTEU", e.target.value)} />
                        </Field>
                        <div className="flex items-center gap-4 pt-6">
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                            <input type="checkbox" checked={zone.isHospitalPen || false} onChange={(e) => updateZone(zone.id, "isHospitalPen", e.target.checked)} className="h-4 w-4 rounded" />
                            Hospital
                          </label>
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer">
                            <input type="checkbox" checked={zone.isSpare || false} onChange={(e) => updateZone(zone.id, "isSpare", e.target.checked)} className="h-4 w-4 rounded" />
                            Spare
                          </label>
                        </div>
                      </div>
                      <div className="mt-3">
                        <Field label="Notes">
                          <input className="input" value={zone.notes} onChange={(e) => updateZone(zone.id, "notes", e.target.value)} placeholder="Special instructions..." />
                        </Field>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* Freight Holds / Deck Space */}
              <SectionCard
                title="Freight Holds / Deck Space"
                description="Configure freight holds, deck areas, or cargo spaces. Not applicable for livestock-only vessels."
                action={
                  <button
                    type="button"
                    onClick={() => setHolds(prev => [...prev, { id: Date.now(), name: "", deckLocation: "", maxWeightKg: "", maxCubeM3: "", maxTEU: "", notes: "" }])}
                    className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                  >
                    Add Hold
                  </button>
                }
              >
                <div className="space-y-4">
                  {holds.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                      No holds configured. Add a hold if this vessel carries freight or has dedicated cargo deck space.
                    </div>
                  ) : null}

                  {holds.map((hold, index) => (
                    <div key={hold.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900 text-sm">{hold.name || `Hold ${index + 1}`}</h3>
                        <button type="button" onClick={() => setHolds(prev => prev.filter(h => h.id !== hold.id))}
                          className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        <Field label="Hold / Deck Name">
                          <input className="input" value={hold.name} onChange={(e) => setHolds(prev => prev.map(h => h.id === hold.id ? { ...h, name: e.target.value } : h))} placeholder="e.g. Main Deck, Hold 1" />
                        </Field>
                        <Field label="Deck Location">
                          <select className="input" value={hold.deckLocation} onChange={(e) => setHolds(prev => prev.map(h => h.id === hold.id ? { ...h, deckLocation: e.target.value } : h))}>
                            <option value="">— Select —</option>
                            <option value="main_deck">Main Deck</option>
                            <option value="lower_deck">Lower Deck</option>
                            <option value="upper_deck">Upper Deck</option>
                            <option value="forward">Forward</option>
                            <option value="aft">Aft</option>
                            <option value="hold">Below Deck Hold</option>
                          </select>
                        </Field>
                        <Field label="Max Weight (kg)">
                          <input className="input" type="number" value={hold.maxWeightKg} onChange={(e) => setHolds(prev => prev.map(h => h.id === hold.id ? { ...h, maxWeightKg: e.target.value } : h))} />
                        </Field>
                        <Field label="Max Cube (m³)">
                          <input className="input" type="number" value={hold.maxCubeM3} onChange={(e) => setHolds(prev => prev.map(h => h.id === hold.id ? { ...h, maxCubeM3: e.target.value } : h))} />
                        </Field>
                        <Field label="Max TEU">
                          <input className="input" type="number" value={hold.maxTEU} onChange={(e) => setHolds(prev => prev.map(h => h.id === hold.id ? { ...h, maxTEU: e.target.value } : h))} />
                        </Field>
                        <Field label="Notes">
                          <input className="input" value={hold.notes} onChange={(e) => setHolds(prev => prev.map(h => h.id === hold.id ? { ...h, notes: e.target.value } : h))} placeholder="Restrictions, notes..." />
                        </Field>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>

            <div className="space-y-6">
              <AsideCard title="Vessel Summary">
                <SummaryRow label="Name" value={form.name || "—"} />
                <SummaryRow label="Code" value={form.code || "—"} />
                <SummaryRow label="Type" value={form.type || "—"} />
                <SummaryRow label="Status" value={form.status || "active"} />
                <SummaryRow label="Routes" value={form.routes || "—"} />
                <SummaryRow
                  label="MMSI"
                  value={form.mmsi || "—"}
                />
              </AsideCard>

              <AsideCard title="Capacity Snapshot">
                <SummaryRow
                  label="Max Weight KG"
                  value={form.maxBookingWeight || "—"}
                />
                <SummaryRow
                  label="Max Livestock"
                  value={form.maxLivestockAllocation || "—"}
                />
                <SummaryRow
                  label="Max Freight / Cube"
                  value={form.maxFreightAllocation || "—"}
                />
                <SummaryRow label="Max TEU" value={form.maxTEU || "—"} />
                <SummaryRow label="Zones" value={String(zones.length)} />
              </AsideCard>

              <AsideCard title="Operating Rules">
                <SummaryRow
                  label="Mixed Cargo Allowed"
                  value={form.mixedCargoAllowed || "Yes"}
                />
                <SummaryRow
                  label="Livestock + Freight"
                  value={form.livestockAndFreightTogether || "Yes"}
                />
                <SummaryRow
                  label="Safety Buffer"
                  value={form.safetyBuffer || "—"}
                />
                <SummaryRow
                  label="Operational Buffer"
                  value={form.operationalBuffer || "—"}
                />
              </AsideCard>
            </div>
          </div>
        </form>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.9rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0.75rem 0.95rem;
          font-size: 0.95rem;
          color: rgb(15 23 42);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .input::placeholder {
          color: rgb(148 163 184);
        }

        .input:hover {
          border-color: rgb(148 163 184);
        }

        .input:focus {
          border-color: rgb(15 23 42);
          box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08);
        }
      `}</style>
    </div>
  );
};

const SectionCard = ({ title, description, children, action }) => (
  <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
    <div className="mb-6 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        ) : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const SettingsGrid = ({ children }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-slate-700">
      {label}
    </label>
    {children}
  </div>
);

const AsideCard = ({ title, children }) => (
  <aside className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
    <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
      {title}
    </h2>
    <div className="mt-4 space-y-3">{children}</div>
  </aside>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3">
    <span className="text-sm font-medium text-slate-500">{label}</span>
    <span className="max-w-[60%] break-words text-right text-sm font-semibold text-slate-900">
      {value}
    </span>
  </div>
);

export default EditVessel;